<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { useCurrentUserQuery } from '@/composables/queries/current_user'
import { usePropertiesQuery } from '@/composables/queries/properties'
import { usePopulateCurrentPropertyId } from '@/composables/use-populate-current-property-id'
import { useAlertsQuery } from '@/composables/queries/alerts'
import { useMetaStore } from '@/store/meta'
import { requiresAuth } from '@/utils/route-meta'
import NotificationsCenter from '@/components/shared/NotificationsCenter.vue'

const route = useRoute()

const transition = ref(route.path.includes('solar-mode') ? 'fade' : '')
const tanstackDevtools = ref(import.meta.env.VITE_DEV_QUERY_DEVTOOLS === 'true')

const isAuthenticatedRoute = computed(() => requiresAuth(route))

const { isPending, data: currentUserData } = useCurrentUserQuery({
  enabled: isAuthenticatedRoute
})
const { data: propertiesData } = usePropertiesQuery({
  enabled: isAuthenticatedRoute
})
useAlertsQuery({ enabled: isAuthenticatedRoute })

onMounted(() => {
  useMetaStore()
})

usePopulateCurrentPropertyId(propertiesData)

watch(
  () => isPending.value,
  () => {
    if (window.heap && currentUserData.value) {
      window.heap.identify(currentUserData.value?.id)
    }
  }
)
</script>

<template>
  <router-view v-slot="{ Component }">
    <transition :name="transition">
      <component :is="Component" />
    </transition>
  </router-view>
  <NotificationsCenter />
  <template v-if="tanstackDevtools">
    <VueQueryDevtools position="left" button-position="bottom-left" />
  </template>
</template>

<style lang="scss">
.fade-enter {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
  opacity: 0;
}
</style>
